import { RadioGroup, snackbar, Select } from '@mobiscroll/react5';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { cancelRazorpaySubscription, createRazorpaySubscription, getUserSubStatus, listAllRazorpayPlans, updateProfile, verifyRazorpaySubscriptionSignature } from './api';
import { useQuery } from 'react-query';
import Loader from '../common/Loader';
import { PlanCard } from './PlanCard';
import signup_icon from "src/assets/Signup icon  human.svg";
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from 'src/features/user/userSlice';
import { useHistory } from 'react-router-dom';
import ExistingSubscription from './ExistingSubscription';
// import { getMyProfile } from '../Profile/api';

// Load Razorpay script dynamically
const loadRazorpayScript = (src) => {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const CreateSubscription = () => {
    const [loader, setLoader] = useState(false);
    const [razorpayLoaded, setRazorpayLoaded] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    const user = useSelector((state) => state.user.user);
    const subStatus = user?.subscriptionStatus || null;

    const history = useHistory();
    const dispatch = useDispatch();

    // load Razorpay script
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                // Load Razorpay script
                const loaded = await loadRazorpayScript("https://checkout.razorpay.com/v1/checkout.js");
                setRazorpayLoaded(loaded);
                if (!loaded) {
                    snackbar({ message: "Failed to load Razorpay. Please refresh the page.", color: "danger", });
                }
            } catch (error) {
                console.error("Error initializing component:", error);
            } finally {
                // setIsCheckingExisting(false);
            }
        };

        initializeComponent();
    }, []);

    //fetch plans
    const { data: plans, isLoading: plansLoading } = useQuery(
        ["fetch subscriptions ids"],
        async () => {
            const res = await listAllRazorpayPlans();
            console.log("Fetched plans:", res);
            return res;
        },
        {
            enabled: true,
            retry: 2,
            onError: (error) => {
                console.error("Error fetching plans:", error);
                snackbar({ message: "Failed to load subscription plans. Please refresh the page.", color: "danger", });
            }
        }
    );

    useEffect(() => {
        console.log("Plan selected", selectedPlan)
    }, [selectedPlan])

    // Handle Razorpay payment success
    const handlePaymentSuccess = async (response) => {
        try {
            console.log("Payment successful:", response);
            const { razorpay_payment_id, razorpay_signature, razorpay_subscription_id } = response || {};
            // Verify subscription signature with backend to mark as authenticated

            await new Promise(resolve => setTimeout(resolve, 1000)); //delay 1 second

            const verified = await verifyRazorpaySubscriptionSignature({
                razorpay_subscription_id: razorpay_subscription_id,
                razorpay_payment_id: razorpay_payment_id,
                razorpay_signature: razorpay_signature,
            });

            if (!verified) {
                // snackbar({ message: "Payment received but verification failed.", color: "danger", });
                console.log("Payment received but verification failed.");
                // return;
            }

            snackbar({ message: "Please wait. Payment is processing..", color: "success", });
            await new Promise(resolve => setTimeout(resolve, 1000)); //delay again for better UX

            const userData = await getUserSubStatus();
            if (!userData || !userData.subscriptionStatus) {
                snackbar({ message: "Could not fetch updated subscription status.", color: "danger" });
                setIsProcessingPayment(false);
                setLoader(false);
                return;
            }
            // Update  redux
            dispatch(updateUser({ field: 'subscriptionStatus', value: userData.subscriptionStatus }));

            // Show success message
            snackbar({ message: "Subscription activated successfully!", color: "success", });
            history.push("/dashboard");

        } catch (error) {
            console.error("Error after payment success:", error);
            snackbar({ message: "Payment successful but failed to update subscription status. Please contact support.", color: "warning", });
        } finally {
            setIsProcessingPayment(false);
            setLoader(false);
        }
    };

    // Handle Razorpay payment failure
    const handlePaymentFailure = (error) => {
        console.error("Payment failed:", error);
        snackbar({ message: "Payment failed. Please try again.", color: "danger", });
        setIsProcessingPayment(false);
        setLoader(false);
    };

    // Create and open Razorpay checkout
    const openRazorpayCheckout = async (subscriptionData) => {
        if (!window.Razorpay) {
            snackbar({ message: "Razorpay not loaded. Please refresh the page.", color: "danger", });
            return;
        }

        const selectedPlanData = plans?.find(plan => plan?.planId === selectedPlan);

        if (!selectedPlanData) {
            snackbar({ message: "Selected plan not found. Please try again.", color: "danger", });
            return;
        }

        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_FAmWQmMl46gSc2",
            subscription_id: subscriptionData.subscriptionId,
            name: "DocGai Clinic",
            description: selectedPlanData.item?.description || "Subscription Plan",
            image: "/docg.png",
            prefill: {
                name: user?.clinicData?.name || "",
                email: user?.clinicData?.mail || "",
                contact: user?.clinicData?.helplineNumber || "",
            },
            notes: {
                clinic_id: user.clinicId || sessionStorage.getItem("clinic-id"),
                plan_id: selectedPlan,
                subscription_type: selectedPlanData.subscriptionType,
            },
            theme: {
                color: "#c06c84",
            },
            handler: handlePaymentSuccess,
            modal: {
                ondismiss: () => {
                    console.log("Payment modal dismissed");
                    setIsProcessingPayment(false);
                    setLoader(false);
                },
            },
        };

        try {
            console.log("Opening Razorpay with options:", options);
            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", handlePaymentFailure);
            rzp.open();
        } catch (error) {
            console.error("Error opening Razorpay:", error);
            snackbar({ message: "Failed to open payment gateway. Please try again.", color: "danger", });
            setIsProcessingPayment(false);
            setLoader(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedPlan) {
            snackbar({ message: "Please select a plan before proceeding.", color: "warning", });
            return;
        }

        if (!razorpayLoaded) {
            snackbar({ message: "Payment gateway is still loading. Please wait.", color: "warning", });
            return;
        }

        const clinicId = user?.clinicId || sessionStorage.getItem("clinic-id");
        if (!clinicId) {
            snackbar({ message: "Clinic not found. Please re-login and try again.", color: "danger", });
            return;
        }

        setLoader(true);
        setIsProcessingPayment(true);

        try {
            // Step 1: Cancel existing subscription if any (only if cancellable)
            const cancellableStatuses = ["active", "authenticated", "created", "paused"];
            if (subStatus && cancellableStatuses.includes(subStatus)) {
                const cancelSuccess = await cancelRazorpaySubscription(clinicId);
                if (!cancelSuccess) {
                    throw new Error("Failed to cancel existing subscription");
                }
            }

            // Step 2: Update profile with selected plan
            const profileRes = await updateProfile({ planId: selectedPlan });
            if (!profileRes) {
                throw new Error("Failed to update profile");
            }

            // Step 3: Create Razorpay subscription on backend
            const subscriptionRes = await createRazorpaySubscription(clinicId);
            if (!subscriptionRes) {
                throw new Error("Failed to create subscription");
            }
            console.log("create subs api res--->", subscriptionRes)

            // Step 4: Open Razorpay checkout
            await openRazorpayCheckout(subscriptionRes);

        } catch (error) {
            console.error("Error in subscription creation:", error);
            snackbar({ message: error.message || "Failed to create subscription. Please try again.", color: "danger", });
        } finally {
            setIsProcessingPayment(false);
            setLoader(false);
        }

    }

    if (subStatus && (subStatus === "authenticated" || subStatus === "active")) {
        return (
            <ExistingSubscription />
        )
    }

    return (
        <>
            <Helmet>
                <title>Create Subscription | DOCG</title>
            </Helmet>
            <Loader show={plansLoading || loader} />

            <div className="mbsc-grid h-100">
                <div className="d-flex justify-content-center align-items-center flex-column w-100">
                    <div className="mbsc-row">
                        <div className="signup-wrapper-inner">

                            <RadioGroup name="group">
                                <div className="mbsc-row d-flex flex-col justify-content-center align-items-center overflow-auto flex-wrap py-2">
                                    <div className="justify-content-center align-items-center w-100 d-flex my-3">
                                        {!selectedPlan && (
                                            <div className="text-center text-muted my-4">
                                                <h2>Please select a plan to view details</h2>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mbsc-col-12">
                                        <Select
                                            data={plans?.map((plan) => ({
                                                value: plan?.planId,
                                                text: `${plan?.subscriptionType} - ₹${Number(plan?.item?.amount / 100).toLocaleString("en-US")}`
                                            }))}
                                            label="Plan Type*"
                                            inputProps={{
                                                inputStyle: "box",
                                                labelStyle: "stacked",
                                                placeholder: "Please select a plan",
                                            }}
                                            touchUi={false}
                                            value={selectedPlan}
                                            onChange={(e) => {
                                                setSelectedPlan(e.value);
                                            }}
                                        />
                                    </div>

                                    <div className="justify-content-center align-items-center w-100 d-flex mt-4">
                                        {selectedPlan && (
                                            <PlanCard
                                                plan={plans?.find(
                                                    (itm) => itm?.planId === selectedPlan
                                                )}
                                                plans={plans}
                                                selectedPlan={selectedPlan}
                                            />
                                        )}
                                    </div>
                                </div>
                            </RadioGroup>

                            <button
                                onClick={handleSubmit}
                                disabled={!selectedPlan || !razorpayLoaded || isProcessingPayment}
                                className={`docg-save-btn d-flex justify-content-center mt-5 mx-auto ${(!selectedPlan || !razorpayLoaded || isProcessingPayment) ? 'disabled' : ''
                                    }`}
                                style={{
                                    opacity: (!selectedPlan || !razorpayLoaded || isProcessingPayment) ? 0.6 : 1,
                                    cursor: (!selectedPlan || !razorpayLoaded || isProcessingPayment) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <img src={signup_icon} alt="" />
                                &nbsp;&nbsp;
                                {isProcessingPayment ? 'Processing...' : 'Subscribe Now'}
                            </button>

                            {!razorpayLoaded && (
                                <div className="text-center text-muted mt-2">
                                    <small>Loading payment gateway...</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CreateSubscription;